import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import { DISCORD_URL } from '../constants/siteInfo';
import { PostHeader, PostSections, TopicIcon, TOPIC_LABEL, formatDate, Icon } from '../components/changelog/changelogUi';

/**
 * /changelog/:slug — egy update saját oldala (a Discord-poszt ide linkel).
 * Nagyobb képek, oldalt az "In this update" lista és a link másolása, alul az
 * előző / következő update.
 */
export default function ChangelogPostPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [copied, setCopied] = useState(false);

  // A lap címe — távozáskor vissza: a többi oldal nem állít sajátot.
  useEffect(() => {
    const prev = document.title;
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    let alive = true;
    setData(null);
    setMissing(false);
    api.get(`/changelog/posts/${encodeURIComponent(slug)}`)
      .then(res => {
        if (!alive) return;
        setData(res.data);
        document.title = `${res.data.post.title} — MortisEngine changelog`;
      })
      .catch(() => { if (alive) setMissing(true); });
    window.scrollTo({ top: 0 });
    return () => { alive = false; };
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* a böngésző nem engedi — a cím a címsorban amúgy is ott van */ }
  };

  if (missing) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col items-start gap-4">
        <Link to="/changelog" className="text-sm text-gray-400 hover:text-white">← Changelog</Link>
        <h1 className="m-0 text-2xl font-semibold text-white">This update does not exist</h1>
        <p className="m-0 text-gray-400">It may have been taken down, or the link is mistyped.</p>
      </div>
    );
  }

  const post = data?.post;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-9 pb-20 flex flex-col gap-[22px]">
      <Link to="/changelog" className="text-sm text-gray-400 hover:text-white self-start">← Changelog</Link>
      {!post && (
        <div className="flex flex-col gap-4" aria-hidden="true">
          <div className="skeleton h-5 w-48" /><div className="skeleton h-10 w-3/4" /><div className="skeleton h-48 w-full" />
        </div>
      )}
      {post && (
        <div className="flex flex-wrap gap-12 items-start">
          <article className="flex-[999_1_640px] min-w-0 flex flex-col gap-[18px]">
            <PostHeader post={post} titleSize="lg" />
            <PostSections post={post} size="lg" />

            <nav aria-label="More updates" className="flex flex-wrap gap-4 justify-between border-t border-line pt-6 mt-3">
              {data.older ? (
                <Link to={`/changelog/${data.older.slug}`} className="card card-interactive flex-[1_1_280px] px-[18px] py-4 flex flex-col gap-1">
                  <span className="panel-label">← Older</span>
                  <span className="text-[15px] font-medium text-white">{data.older.title}</span>
                  <span className="font-mono text-xs text-gray-400">{formatDate(data.older.publishedAt)}</span>
                </Link>
              ) : (
                <span className="card flex-[1_1_280px] px-[18px] py-4 flex flex-col gap-1 opacity-55">
                  <span className="panel-label">← Older</span>
                  <span className="text-[15px] text-gray-400">This is the first update</span>
                </span>
              )}
              {data.newer ? (
                <Link to={`/changelog/${data.newer.slug}`} className="card card-interactive flex-[1_1_280px] px-[18px] py-4 flex flex-col gap-1 items-end text-right">
                  <span className="panel-label">Newer →</span>
                  <span className="text-[15px] font-medium text-white">{data.newer.title}</span>
                  <span className="font-mono text-xs text-gray-400">{formatDate(data.newer.publishedAt)}</span>
                </Link>
              ) : (
                <span className="card flex-[1_1_280px] px-[18px] py-4 flex flex-col gap-1 items-end opacity-55">
                  <span className="panel-label">Newer →</span>
                  <span className="text-[15px] text-gray-400">This is the latest update</span>
                </span>
              )}
            </nav>
          </article>

          <aside className="flex-[1_1_260px] max-w-full md:max-w-[300px] flex flex-col gap-4">
            {post.sections.length > 1 && (
              <div className="card px-3.5 py-4">
                <span className="panel-label block px-2.5 pb-2">In this update</span>
                <ul className="list-none m-0 p-0 flex flex-col gap-0.5">
                  {post.sections.map((s, i) => (
                    <li key={i}>
                      <a href={`#t${i}`} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-200 hover:bg-[#181a1e]">
                        <span className="text-gray-400 flex"><TopicIcon topic={s.topic} className="w-[15px] h-[15px]" /></span>
                        <span className="min-w-0">{s.title || TOPIC_LABEL[s.topic]}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="card px-[18px] py-4 flex flex-col gap-2.5">
              <span className="panel-label">Share</span>
              <button type="button" onClick={copyLink} className="btn-secondary h-9 py-0 text-[13px]">
                <Icon className="w-[15px] h-[15px]">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </Icon>
                {copied ? 'Link copied' : 'Copy link'}
              </button>
              <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost h-9 py-0 text-[13px]">
                Discuss on Discord ↗
              </a>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
